import styled from '@emotion/styled';
import { InlineTagNodeWrap } from '../card/CommonCardStyles';
import { IconName } from '../images/IconMap';
import { LabelMedium, LightXSmall } from '../text/Typography';
import {
    ButtonRow,
    LinkRow,
    RowChildrenContainer,
    RowContent,
    RowEndIcon,
    RowStartIcon,
    RowWrapper,
} from './CommonRowStyles';

interface IActionRowProps {
    title: string;
    description?: string;
    disabled?: boolean;
    startComponent?: React.ReactNode;
    startIcon?: IconName;
    endIcon?: IconName;
    iconSize?: number;
    href?: string;
    onClick?: (
        event:
            | React.MouseEvent<HTMLButtonElement>
            | React.KeyboardEvent<HTMLButtonElement>
            | React.TouchEvent<HTMLButtonElement>
    ) => void;
    children?: React.ReactNode;
    tagNode?: React.ReactNode;
    borderless?: boolean;
}

export default function ActionRow({
    title,
    description,
    disabled = false,
    startComponent,
    startIcon,
    endIcon = 'forward',
    href,
    iconSize = 42,
    onClick,
    children,
    tagNode,
    borderless = false,
    ...restProps
}: IActionRowProps) {
    const commonProps = {
        disabled,
    };
    if (!href && !onClick) throw new Error('ActionRow requires either an href or onClick prop');

    if (href) {
        return (
            <RowWrapper borderless={borderless}>
                <LinkRow href={href} {...commonProps} {...restProps}>
                    {startIcon && <RowStartIcon name={startIcon} size={iconSize} />}
                    {startComponent}
                    <RowContent>
                        {tagNode ? (
                            <InlineTagNodeWrap>
                                {tagNode} <ActionRowTitle as="span">{title}</ActionRowTitle>
                            </InlineTagNodeWrap>
                        ) : (
                            <ActionRowTitle as="span">{title}</ActionRowTitle>
                        )}
                        {description && <LightXSmall as="span">{description}</LightXSmall>}
                        {children && <RowChildrenContainer>{children}</RowChildrenContainer>}
                    </RowContent>
                    {endIcon && <RowEndIcon name={endIcon} size={iconSize} />}
                </LinkRow>
            </RowWrapper>
        );
    } else {
        return (
            <RowWrapper borderless={borderless}>
                <ButtonRow onClick={onClick} {...commonProps} {...restProps}>
                    {startIcon && <RowStartIcon name={startIcon} size={iconSize} />}
                    {startComponent}
                    <RowContent>
                        {tagNode ? (
                            <InlineTagNodeWrap>
                                {tagNode} <ActionRowTitle as="span">{title}</ActionRowTitle>
                            </InlineTagNodeWrap>
                        ) : (
                            <ActionRowTitle as="span">{title}</ActionRowTitle>
                        )}
                        {description && <LightXSmall as="span">{description}</LightXSmall>}
                        {children && <RowChildrenContainer>{children}</RowChildrenContainer>}
                    </RowContent>
                    {endIcon && <RowEndIcon name={endIcon} size={iconSize} />}
                </ButtonRow>
            </RowWrapper>
        );
    }
}

const ActionRowTitle = styled(LabelMedium)`
    color: inherit;
`;
